import { API_BASE_URL, API_BASE_URL_CANDIDATES, API_ENDPOINTS, buildApiUrl } from '../config/api';
import { fallbackContentLists, fallbackSettings } from '../data/cmsFallbacks';
import { fetchWithTimeout } from './http';

const responseCache = new Map();
const pendingRequests = new Map();
const PERSISTED_CACHE_STORAGE_KEY = 'kiuf_frontend_api_cache_v2';
const REVISION_STORAGE_KEY = 'kiuf_frontend_content_revision_v1';
const BACKEND_UNAVAILABLE_STORAGE_KEY = 'kiuf_frontend_backend_unavailable_until_v1';
const MAX_PERSISTED_CACHE_ENTRIES = 40;
const MAX_PERSISTED_ENTRY_BYTES = 128 * 1024;
const MAX_PERSISTED_CACHE_BYTES = 1024 * 1024;
const REVISION_POLL_INTERVAL_MS = 30 * 1000;
const BACKEND_RETRY_DELAY_MS = 30 * 1000;
let persistedCacheLoaded = false;
let persistTimer = null;
let currentRevisionSignature = '';
let revisionPollTimer = null;
let pendingRevisionRequest = null;
let backendUnavailableUntil = 0;
let hasLoggedRevisionRefreshError = false;
const revisionListeners = new Set();
// Admin panelda saqlangan o'zgarishlar (masalan, fasl effekti) saytda darhol ko'rinishi
// uchun revision tekshiruvi har doim yoqiq. Faqat aniq 'false' berilsa o'chiriladi.
const isCmsLiveRevalidationEnabled = process.env.REACT_APP_ENABLE_CMS_LIVE_REVALIDATION !== 'false';

// Menyu daraxtlari va sozlamalar localStorage da ham saqlanadi: sayt qayta
// ochilganda navbar API javobini kutmasdan darhol chiziladi, keyin fonda yangilanadi.
const DURABLE_STORAGE_PREFIX = 'kiuf_frontend_durable_v1:';
const DURABLE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const DURABLE_MAX_ENTRY_CHARS = 200 * 1024;

const DEFAULT_CACHE_TTL_MS = 60 * 1000;
const LONG_CACHE_TTL_MS = 5 * 60 * 1000;

function getCacheTtl(path = '') {
  if (path.startsWith('/api/settings/')) {
    return LONG_CACHE_TTL_MS;
  }

  if (path.includes('/tree?') || path.includes('faculty-page-menus') || path.includes('academic-menus')) {
    return LONG_CACHE_TTL_MS;
  }

  return DEFAULT_CACHE_TTL_MS;
}

function cloneFallbackValue(value) {
  if (value === null || value === undefined) {
    return value;
  }

  return JSON.parse(JSON.stringify(value));
}

function isBackendRetryDelayed() {
  if (backendUnavailableUntil <= Date.now()) {
    const storedUntil = readStoredBackendUnavailableUntil();

    if (storedUntil > Date.now()) {
      backendUnavailableUntil = storedUntil;
    }
  }

  return backendUnavailableUntil > Date.now();
}

function markBackendUnavailable() {
  backendUnavailableUntil = Date.now() + BACKEND_RETRY_DELAY_MS;
  writeStoredBackendUnavailableUntil(backendUnavailableUntil);
}

function clearBackendUnavailable() {
  backendUnavailableUntil = 0;
  writeStoredBackendUnavailableUntil(0);
}

function parseCmsPath(path = '') {
  const [pathname] = String(path || '').split('?');

  if (pathname === API_ENDPOINTS.contentRevision()) {
    return { type: 'contentRevision' };
  }

  if (pathname.startsWith('/api/settings/')) {
    return {
      type: 'setting',
      key: decodeURIComponent(pathname.slice('/api/settings/'.length))
    };
  }

  if (!pathname.startsWith('/api/content/')) {
    return { type: 'unknown' };
  }

  const segments = pathname
    .slice('/api/content/'.length)
    .split('/')
    .filter(Boolean);

  if (!segments.length) {
    return { type: 'unknown' };
  }

  if (segments[1] === 'tree') {
    return {
      type: 'contentTree',
      resource: segments[0]
    };
  }

  if (segments.length === 1) {
    return {
      type: 'contentList',
      resource: segments[0]
    };
  }

  return {
    type: 'contentItem',
    resource: segments[0],
    id: segments.slice(1).join('/')
  };
}

function buildOfflineResponse(path = '') {
  const descriptor = parseCmsPath(path);

  if (descriptor.type === 'setting') {
    return {
      item: cloneFallbackValue(fallbackSettings[descriptor.key] ?? null)
    };
  }

  if (descriptor.type === 'contentList') {
    return {
      items: cloneFallbackValue(fallbackContentLists[descriptor.resource] ?? [])
    };
  }

  if (descriptor.type === 'contentTree') {
    return {
      items: []
    };
  }

  if (descriptor.type === 'contentItem') {
    return {
      item: null
    };
  }

  return undefined;
}

function canUseSessionStorage() {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function canUseDom() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function readStoredBackendUnavailableUntil() {
  if (!canUseSessionStorage()) {
    return 0;
  }

  try {
    const value = Number(window.sessionStorage.getItem(BACKEND_UNAVAILABLE_STORAGE_KEY) || 0);
    return Number.isFinite(value) ? value : 0;
  } catch (_error) {
    return 0;
  }
}

function writeStoredBackendUnavailableUntil(value = 0) {
  if (!canUseSessionStorage()) {
    return;
  }

  try {
    if (value > Date.now()) {
      window.sessionStorage.setItem(BACKEND_UNAVAILABLE_STORAGE_KEY, String(value));
      return;
    }

    window.sessionStorage.removeItem(BACKEND_UNAVAILABLE_STORAGE_KEY);
  } catch (_error) {
    // ignore
  }
}

function ensurePersistedCacheLoaded() {
  if (persistedCacheLoaded || !canUseSessionStorage()) {
    return;
  }

  persistedCacheLoaded = true;

  try {
    window.sessionStorage.removeItem('kiuf_frontend_api_cache_v1');
    const raw = window.sessionStorage.getItem(PERSISTED_CACHE_STORAGE_KEY);

    if (!raw) {
      return;
    }

    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      window.sessionStorage.removeItem(PERSISTED_CACHE_STORAGE_KEY);
      return;
    }

    parsed.forEach((entry) => {
      if (!Array.isArray(entry) || entry.length !== 2) {
        return;
      }

      const [path, value] = entry;

      if (
        typeof path === 'string' &&
        value &&
        typeof value === 'object' &&
        Number.isFinite(value.expiresAt) && value.expiresAt > Date.now()
      ) {
        responseCache.set(path, value);
      }
    });
  } catch (_error) {
    try {
      window.sessionStorage.removeItem(PERSISTED_CACHE_STORAGE_KEY);
    } catch (_storageError) {
      // ignore
    }
  }
}

function persistCacheSnapshot() {
  if (persistTimer !== null || !canUseSessionStorage()) return;
  persistTimer = window.setTimeout(() => {
    persistTimer = null;
    flushCacheSnapshot();
  }, 200);
}

function flushCacheSnapshot() {
  if (!canUseSessionStorage()) {
    return;
  }

  try {
    let storedBytes = 0;
    const serializableEntries = Array.from(responseCache.entries())
      .filter(([, value]) => value?.persist !== false && value.expiresAt > Date.now())
      .sort((left, right) => Number(right[1]?.updatedAt || 0) - Number(left[1]?.updatedAt || 0))
      .slice(0, MAX_PERSISTED_CACHE_ENTRIES)
      .filter((entry) => {
        storedBytes += entry[1].size || 0;
        return storedBytes <= MAX_PERSISTED_CACHE_BYTES;
      });

    window.sessionStorage.setItem(PERSISTED_CACHE_STORAGE_KEY, JSON.stringify(serializableEntries));
  } catch (_error) {
    try {
      window.sessionStorage.removeItem(PERSISTED_CACHE_STORAGE_KEY);
    } catch (_storageError) {
      // ignore
    }
  }
}

function readStoredRevisionSignature() {
  if (!canUseSessionStorage()) {
    return '';
  }

  try {
    return String(window.sessionStorage.getItem(REVISION_STORAGE_KEY) || '');
  } catch (_error) {
    return '';
  }
}

function writeStoredRevisionSignature(signature = '') {
  if (!canUseSessionStorage()) {
    return;
  }

  try {
    if (signature) {
      window.sessionStorage.setItem(REVISION_STORAGE_KEY, signature);
      return;
    }

    window.sessionStorage.removeItem(REVISION_STORAGE_KEY);
  } catch (_error) {
    // ignore
  }
}

function getResponseCacheTtl(path = '', response) {
  const cacheControl = response?.headers?.get('cache-control') || '';
  const maxAgeMatch = cacheControl.match(/max-age=(\d+)/i);
  const maxAgeSeconds = maxAgeMatch ? Number(maxAgeMatch[1]) : 0;

  if (Number.isFinite(maxAgeSeconds) && maxAgeSeconds > 0) {
    return maxAgeSeconds * 1000;
  }

  return getCacheTtl(path);
}

function readCachedResponse(path) {
  ensurePersistedCacheLoaded();
  const cached = responseCache.get(path);

  if (!cached) {
    return undefined;
  }

  if (cached.expiresAt <= Date.now()) {
    responseCache.delete(path);
    persistCacheSnapshot();
    return undefined;
  }

  return cached.data;
}

export function clearCmsCache() {
  responseCache.clear();
  pendingRequests.clear();
  persistedCacheLoaded = false;

  if (!canUseSessionStorage()) {
    return;
  }

  try {
    window.clearTimeout(persistTimer);
    persistTimer = null;
    window.sessionStorage.removeItem(PERSISTED_CACHE_STORAGE_KEY);
  } catch (_error) {
    // ignore
  }
}

function writeCachedResponse(path, data, ttlMs = getCacheTtl(path)) {
  let size = 0;
  const shouldPersist = (() => {
    try {
      size = JSON.stringify(data).length * 2;
      return size <= MAX_PERSISTED_ENTRY_BYTES;
    } catch (_error) {
      return false;
    }
  })();

  if (responseCache.size >= 60) responseCache.delete(responseCache.keys().next().value);
  responseCache.set(path, {
    data,
    expiresAt: Date.now() + ttlMs,
    updatedAt: Date.now(),
    persist: shouldPersist,
    size
  });

  persistCacheSnapshot();

  return data;
}

function writeOfflineResponse(path, response) {
  return writeCachedResponse(
    path,
    response,
    Math.min(getCacheTtl(path), BACKEND_RETRY_DELAY_MS)
  );
}

function canUseLocalStorage() {
  try {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  } catch (_error) {
    return false;
  }
}

// Har bir sahifada (navbar, bosh sahifa banneri) birinchi ekranda kerak bo'ladigan,
// kam o'zgaradigan va hajmi kichik javoblar.
const DURABLE_LIST_RESOURCES = new Set(['banners']);

function isDurablePath(path = '') {
  const [pathname] = String(path || '').split('?');

  if (pathname.endsWith('/tree') || pathname.startsWith('/api/settings/')) {
    return true;
  }

  const descriptor = parseCmsPath(pathname);
  return descriptor.type === 'contentList' && DURABLE_LIST_RESOURCES.has(descriptor.resource);
}

function writeDurableResponse(path, data) {
  if (!isDurablePath(path) || !canUseLocalStorage()) {
    return;
  }

  try {
    const serialized = JSON.stringify({ savedAt: Date.now(), data });

    if (serialized.length > DURABLE_MAX_ENTRY_CHARS) {
      return;
    }

    window.localStorage.setItem(`${DURABLE_STORAGE_PREFIX}${path}`, serialized);
  } catch (_error) {
    // Storage to'lgan yoki bloklangan bo'lsa, oddiy kesh bilan ishlashda davom etamiz.
  }
}

function readDurableResponse(path) {
  if (!isDurablePath(path) || !canUseLocalStorage()) {
    return undefined;
  }

  try {
    const raw = window.localStorage.getItem(`${DURABLE_STORAGE_PREFIX}${path}`);

    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw);

    if (!parsed || !Number.isFinite(parsed.savedAt) || Date.now() - parsed.savedAt > DURABLE_MAX_AGE_MS) {
      window.localStorage.removeItem(`${DURABLE_STORAGE_PREFIX}${path}`);
      return undefined;
    }

    return parsed.data;
  } catch (_error) {
    return undefined;
  }
}

// Eskirgan bo'lishi mumkin bo'lgan, lekin darhol ko'rsatsa bo'ladigan menyu daraxti.
export function getStaleContentTree(resource, query = {}) {
  const data = readDurableResponse(API_ENDPOINTS.contentTree(resource, query));
  return Array.isArray(data?.items) ? data.items : null;
}

export function getStaleContentList(resource, query = {}) {
  const data = readDurableResponse(API_ENDPOINTS.contentList(resource, query));
  return Array.isArray(data?.items) ? data.items : null;
}

export function getStaleSetting(key) {
  if (!key) {
    return null;
  }

  const data = readDurableResponse(API_ENDPOINTS.setting(key));
  return data?.item || null;
}

function buildRevisionSignature(value = {}) {
  return `${Number(value?.revision || 0)}:${String(value?.updatedAt || '')}`;
}

function normalizeMediaUrl(value, baseUrl) {
  if (typeof value !== 'string' || !value.startsWith('/api/')) {
    return value;
  }

  return buildApiUrl(value, baseUrl);
}

function normalizeCmsPayload(value, baseUrl) {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeCmsPayload(item, baseUrl));
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  return Object.entries(value).reduce((accumulator, [key, itemValue]) => {
    if ((key === 'image' || key === 'imageUrl') && typeof itemValue === 'string') {
      accumulator[key] = normalizeMediaUrl(itemValue, baseUrl);
      return accumulator;
    }

    accumulator[key] = normalizeCmsPayload(itemValue, baseUrl);
    return accumulator;
  }, {});
}

export function getCachedContentList(resource, query = {}) {
  const data = readCachedResponse(API_ENDPOINTS.contentList(resource, query));
  return data?.items || null;
}

export function getCachedContentTree(resource, query = {}) {
  const data = readCachedResponse(API_ENDPOINTS.contentTree(resource, query));
  return data?.items || null;
}

export function getCachedContentItem(resource, id) {
  if (!id) {
    return null;
  }

  const data = readCachedResponse(API_ENDPOINTS.contentItem(resource, id));
  return data?.item || null;
}

export function getCachedSetting(key) {
  if (!key) {
    return null;
  }

  const data = readCachedResponse(API_ENDPOINTS.setting(key));
  return data?.item || null;
}

async function request(path, options = {}) {
  const force = options?.force === true;
  const pendingKey = `${force ? 'force' : 'default'}:${path}`;

  if (!force) {
    const cached = readCachedResponse(path);

    if (cached !== undefined) {
      return cached;
    }
  }

  const pending = pendingRequests.get(pendingKey);

  if (pending) {
    return pending;
  }

  if (isBackendRetryDelayed()) {
    const cached = readCachedResponse(path);

    if (cached !== undefined) {
      return cached;
    }

    const offlineResponse = buildOfflineResponse(path);

    if (offlineResponse !== undefined) {
      return writeOfflineResponse(path, offlineResponse);
    }
  }

  const requestPromise = (async () => {
    const errors = [];

    for (const baseUrl of API_BASE_URL_CANDIDATES) {
      try {
        const response = await fetchWithTimeout(buildApiUrl(path, baseUrl), {
          cache: 'no-store'
        });

        if (!response.ok) {
          const error = new Error(`Request failed: ${response.status}`);
          error.status = response.status;
          throw error;
        }

        const data = normalizeCmsPayload(await response.json(), baseUrl);
        clearBackendUnavailable();
        writeDurableResponse(path, data);
        return writeCachedResponse(path, data, getResponseCacheTtl(path, response));
      } catch (error) {
        if (error?.status) {
          throw error;
        }

        errors.push(error);
      }
    }

    markBackendUnavailable();

    const cached = readCachedResponse(path);

    if (cached !== undefined) {
      return cached;
    }

    const offlineResponse = buildOfflineResponse(path);

    if (offlineResponse !== undefined) {
      return writeOfflineResponse(path, offlineResponse);
    }

    const networkError = new Error('Backend server bilan aloqa yo‘q.');
    networkError.cause = errors[errors.length - 1];
    throw networkError;
  })();

  pendingRequests.set(pendingKey, requestPromise);

  try {
    return await requestPromise;
  } finally {
    pendingRequests.delete(pendingKey);
  }
}

async function requestContentRevision() {
  if (isBackendRetryDelayed()) {
    return null;
  }

  const errors = [];

  for (const baseUrl of API_BASE_URL_CANDIDATES) {
    try {
      const response = await fetchWithTimeout(buildApiUrl(API_ENDPOINTS.contentRevision(), baseUrl), {
        cache: 'no-store'
      });

      if (!response.ok) {
        const error = new Error(`Request failed: ${response.status}`);
        error.status = response.status;
        throw error;
      }

      clearBackendUnavailable();
      return response.json();
    } catch (error) {
      if (error?.status) {
        throw error;
      }

      errors.push(error);
    }
  }

  markBackendUnavailable();
  return null;
}

async function pollContentRevision() {
  if (!canUseDom()) {
    return null;
  }

  if (pendingRevisionRequest) {
    return pendingRevisionRequest;
  }

  pendingRevisionRequest = (async () => {
    try {
      const revision = await requestContentRevision();

      if (!revision) {
        return null;
      }

      const nextSignature = buildRevisionSignature(revision);
      const previousSignature = currentRevisionSignature || readStoredRevisionSignature();

      hasLoggedRevisionRefreshError = false;
      currentRevisionSignature = nextSignature;
      writeStoredRevisionSignature(nextSignature);

      if (previousSignature && previousSignature !== nextSignature) {
        clearCmsCache();
        revisionListeners.forEach((listener) => listener(revision));
      }

      return revision;
    } catch (error) {
      if (!hasLoggedRevisionRefreshError) {
        console.error('Failed to refresh content revision:', error);
        hasLoggedRevisionRefreshError = true;
      }
      return null;
    } finally {
      pendingRevisionRequest = null;
    }
  })();

  return pendingRevisionRequest;
}

function handleRevisionVisibilityChange() {
  if (document.visibilityState === 'visible') {
    void pollContentRevision();
  }
}

function startRevisionPolling() {
  if (!canUseDom() || revisionPollTimer || !revisionListeners.size) {
    return;
  }

  window.addEventListener('focus', handleRevisionVisibilityChange);
  document.addEventListener('visibilitychange', handleRevisionVisibilityChange);

  revisionPollTimer = window.setInterval(() => {
    if (document.visibilityState === 'visible') {
      void pollContentRevision();
    }
  }, REVISION_POLL_INTERVAL_MS);

  void pollContentRevision();
}

function stopRevisionPolling() {
  if (!canUseDom() || revisionListeners.size || !revisionPollTimer) {
    return;
  }

  window.clearInterval(revisionPollTimer);
  revisionPollTimer = null;
  window.removeEventListener('focus', handleRevisionVisibilityChange);
  document.removeEventListener('visibilitychange', handleRevisionVisibilityChange);
}

export function subscribeToCmsChanges(listener) {
  if (
    typeof listener !== 'function' ||
    !canUseDom() ||
    !isCmsLiveRevalidationEnabled
  ) {
    return () => {};
  }

  revisionListeners.add(listener);
  startRevisionPolling();

  return () => {
    revisionListeners.delete(listener);
    stopRevisionPolling();
  };
}

export async function fetchContentList(resource, query = {}, options = {}) {
  const data = await request(API_ENDPOINTS.contentList(resource, query), options);
  return data.items || [];
}

export async function fetchContentTree(resource, query = {}, options = {}) {
  const data = await request(API_ENDPOINTS.contentTree(resource, query), options);
  return data.items || [];
}

export async function fetchContentItem(resource, id, options = {}) {
  const data = await request(API_ENDPOINTS.contentItem(resource, id), options);
  return data.item || null;
}

export async function fetchSetting(key, options = {}) {
  try {
    const data = await request(API_ENDPOINTS.setting(key), options);
    return data.item || null;
  } catch (error) {
    if (error.status === 404) {
      return null;
    }

    throw error;
  }
}

export { API_BASE_URL };
